<template>
  <v-container class="client-login" fluid>
    <locale-select class="client-login__locale"></locale-select>
    <v-row justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card outlined class="client-login__card">
          <!-- REQ-10: бренд — только из подписанного кода компании; без него страница нейтральная. -->
          <header class="client-login__brand" data-testid="client-login-brand">
            <span class="client-login__monogram" aria-hidden="true">{{ monogram }}</span>
            <div class="client-login__brand-copy">
              <h1 class="client-login__title" data-testid="client-login-title">{{ title }}</h1>
              <a
                v-if="brand && brand.phone"
                :href="phoneHref"
                class="client-login__phone"
                data-testid="client-login-phone"
              >{{ brand.phone }}</a>
              <a
                v-if="brand && brand.email"
                :href="`mailto:${brand.email}`"
                class="client-login__email"
                data-testid="client-login-email"
                data-field="company.email"
              >{{ brand.email }}</a>
            </div>
          </header>
          <v-card-text class="pa-6">
            <form novalidate data-testid="client-login-form" @submit.prevent="submit">
              <v-text-field
                ref="bookingIdField"
                v-model="bookingId"
                data-testid="client-login-booking-id"
                name="booking_id"
                inputmode="numeric"
                autocomplete="off"
                :error="invalid.bookingId"
                :aria-invalid="invalid.bookingId ? 'true' : null"
                :aria-describedby="error ? errorId : null"
                :label="$t('client_page.login.booking_id')"
                :hint="$t('client_page.login.booking_id_hint')"
                persistent-hint
                outlined
                class="mb-3"
              ></v-text-field>
              <v-text-field
                ref="lastNameField"
                v-model="lastName"
                data-testid="client-login-last-name"
                name="last_name"
                autocomplete="family-name"
                autocapitalize="words"
                :error="invalid.lastName"
                :aria-invalid="invalid.lastName ? 'true' : null"
                :aria-describedby="error ? errorId : null"
                :label="$t('client_page.login.last_name')"
                :hint="$t('client_page.login.last_name_hint')"
                persistent-hint
                outlined
                class="mb-3"
              ></v-text-field>

              <!-- Ошибки — строкой в форме, не тостом (REQ-10). -->
              <p
                v-if="error"
                :id="errorId"
                class="client-login__error"
                role="alert"
                data-testid="client-login-error"
              >{{ error }}</p>

              <v-btn
                type="submit"
                color="primary"
                block
                x-large
                depressed
                :disabled="submitting"
                data-testid="client-login-submit"
              >{{ submitting ? $t("client_page.login.checking") : $t("client_page.login.submit") }}</v-btn>
            </form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// FT-200 STEP-04 (REQ-07, REQ-10): вход в кабинет клиента по номеру бронирования и фамилии.
//
// Компонент сам входит (POST find_booking_for_client_v2), сам кладёт пару во вкладку
// (clientCabinetSession) и отдаёт ответ родителю событием `logged-in` — так его без изменений
// встраивают и кабинет (ClientPage), и страница возврата из банка (Task 6). Входит в «набор
// возврата»: не импортирует компоненты кабинета, стор и роутер.
//
// Бренд проката (название и телефон) — только по подписанному коду компании из ссылки проката
// (`GET /api/v1/client_cabinet/brand?code=`). По номеру бронирования до входа сервер не раскрывает
// ничего: иначе вход стал бы оракулом «номер → прокат». Любой отказ запроса бренда — нейтральная
// страница без ошибки.
//
// «Не найдено» — одно на все отказы: какое поле неверно, сервер не говорит, и форма не угадывает.
import clientCabinetSession from "@/utils/clientCabinetSession";
import { isTransientNetworkError, rateLimitRetryMinutes } from "@/utils/networkErrors";
import { redactedRequestError } from "@/utils/clientCabinetCore";
import LocaleSelect from "@/components/common/LocaleSelect.vue";
import { telHref } from "@/lib/phone/display";

export const BRAND_PATH = "/api/v1/client_cabinet/brand";
export const LOGIN_PATH = "/api/v1/find_booking_for_client_v2";

const DIGITS_ONLY = /^\d+$/;

function text(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

export default {
  name: "ClientLogin",
  components: { LocaleSelect },
  props: {
    // Подписанный код компании (`c` из ссылки проката); без него — «Ваше бронирование».
    companyCode: { type: String, default: null },
    initialBookingId: { type: [String, Number], default: "" },
    initialLastName: { type: String, default: "" },
    // Пара пришла из ссылки или из вкладки — входить сразу, не дожидаясь кнопки.
    autoSubmit: { type: Boolean, default: false },
  },
  data() {
    return {
      bookingId: text(this.initialBookingId),
      lastName: text(this.initialLastName),
      brand: null,
      error: null,
      // Поля, на которые указывает ошибка: aria-invalid и подсветка (REQ-13).
      invalid: { bookingId: false, lastName: false },
      submitting: false,
    };
  },
  computed: {
    title() {
      return (this.brand && this.brand.name) || this.$t("client_page.login.title_neutral");
    },
    monogram() {
      if (!this.brand) return "RP";
      const words = this.title.trim().split(/\s+/).filter(Boolean);
      return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2)).toLocaleUpperCase();
    },
    errorId() {
      return `client-login-error-${this._uid}`;
    },
    phoneHref() {
      const phone = (this.brand && this.brand.phone) || "";
      return telHref(phone);
    },
  },
  created() {
    if (this.companyCode) this.loadBrand();
    if (this.autoSubmit && this.bookingId && this.lastName) this.submit();
  },
  methods: {
    loadBrand() {
      return this.axios
        .get(BRAND_PATH, { params: { code: this.companyCode } })
        .then((response) => {
          const data = (response && response.data) || {};
          if (data.name) {
            this.brand = {
              name: String(data.name),
              phone: data.phone ? String(data.phone) : null,
              email: data.email ? String(data.email) : null,
            };
          }
        })
        .catch(() => {
          // 429, сеть, 5xx — страница остаётся нейтральной: бренд не обязателен для входа.
        });
    },
    // Ошибка проверки в браузере: текст и неверные поля (первое из них получит фокус).
    validate(bookingId, lastName) {
      if (!bookingId || !lastName) {
        return {
          text: this.$t("client_page.login.errors.required"),
          invalid: { bookingId: !bookingId, lastName: !lastName },
        };
      }
      if (!DIGITS_ONLY.test(bookingId)) {
        return {
          text: this.$t("client_page.login.errors.booking_id_not_number"),
          invalid: { bookingId: true, lastName: false },
        };
      }
      return null;
    },
    showError(text, invalid, focusField) {
      this.error = text;
      this.invalid = invalid;
      this.$nextTick(() => this.focusField(focusField));
    },
    focusField(name) {
      const field = this.$refs[name];
      if (field && typeof field.focus === "function") field.focus();
    },
    // «Не найдено» — неверной может быть любая половина пары; какая — не раскрываем, помечаем обе.
    showNotFound(bookingId, lastName) {
      // Неподтверждённая пара этой вкладки (из ссылки) иначе повторялась бы при каждой перезагрузке, тратя лимит.
      const stored = clientCabinetSession.load();
      if (stored && stored.bookingId === bookingId && stored.lastName === lastName) clientCabinetSession.clear();
      this.showError(this.$t("client_page.login.errors.not_found"), { bookingId: true, lastName: true }, "bookingIdField");
    },
    submit() {
      if (this.submitting) return Promise.resolve();
      const bookingId = text(this.bookingId);
      const lastName = text(this.lastName);
      const failure = this.validate(bookingId, lastName);
      if (failure) {
        this.showError(failure.text, failure.invalid, failure.invalid.bookingId ? "bookingIdField" : "lastNameField");
        return Promise.resolve();
      }
      this.error = null;
      this.invalid = { bookingId: false, lastName: false };

      this.submitting = true;
      return this.axios
        .post(LOGIN_PATH, { booking_id: bookingId, last_name: lastName })
        .then((response) => {
          const data = (response && response.data) || {};
          if (data.error || !data.booking) {
            this.showNotFound(bookingId, lastName);
            return;
          }
          clientCabinetSession.save({ bookingId, lastName });
          this.$emit("logged-in", { bookingId, lastName, data });
        })
        .catch((error) => {
          const status = error && error.response && error.response.status;
          // 422 — сервер не получил пару; для человека это тот же «не найдено».
          if (status === 422 || status === 404) {
            this.showNotFound(bookingId, lastName);
            return;
          }
          // Отказ сервера не про поля: поля не помечаем, фокус — в начало формы, к номеру.
          this.showError(this.requestErrorText(error), { bookingId: false, lastName: false }, "bookingIdField");
        })
        .then(() => {
          this.submitting = false;
        });
    },
    requestErrorText(error) {
      const status = error && error.response && error.response.status;
      if (status === 429) {
        const minutes = rateLimitRetryMinutes(error);
        return minutes === null
          ? this.$t("client_page.login.errors.rate_limited")
          : this.$t("client_page.login.errors.rate_limited_retry", { minutes });
      }
      if (!status && isTransientNetworkError(error)) return this.$t("client_page.login.errors.network");
      // В отчёт — без тела запроса: в config.data пара номер + фамилия.
      if (typeof this.$reportError === "function") this.$reportError("ClientLogin: find_booking_for_client_v2", redactedRequestError(error));
      return this.$t("client_page.login.errors.failed");
    },
  },
};
</script>

<style>
/* REQ-13: видимое кольцо фокуса — #1976d2 (4,6:1 на белом), в тёмной теме #64b5f6 (8:1). */
.client-login .v-btn:focus-visible,
.client-login a:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.theme--dark .client-login .v-btn:focus-visible,
.theme--dark .client-login a:focus-visible {
  outline-color: #64b5f6;
}

.client-login {
  --client-auth-bg: #f5f5f5;
  --client-auth-fg: rgba(0, 0, 0, 0.87);
  --client-auth-muted: rgba(0, 0, 0, 0.6);
  --client-auth-surface: #ffffff;
  --client-auth-line: #e0e0e0;
  --client-auth-accent: #1976d2;
  --client-auth-accent-ink: #ffffff;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  padding-top: clamp(24px, 8vh, 80px);
  background: var(--client-auth-bg);
  color: var(--client-auth-fg);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  position: relative;
}

.client-login .client-login__locale {
  position: absolute;
  z-index: 1;
  top: 12px;
  right: 16px;
}

.theme--dark .client-login {
  --client-auth-bg: #121212;
  --client-auth-fg: rgba(255, 255, 255, 0.87);
  --client-auth-muted: rgba(255, 255, 255, 0.6);
  --client-auth-surface: #1e1e1e;
  --client-auth-line: rgba(255, 255, 255, 0.12);
  --client-auth-accent: #2196f3;
  --client-auth-accent-ink: rgba(0, 0, 0, 0.87);
}

.client-login .client-login__card {
  overflow: hidden;
  border-color: var(--client-auth-line) !important;
  border-radius: 24px !important;
  background: var(--client-auth-surface) !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12) !important;
}

.client-login .client-login__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px;
  border-bottom: 1px solid var(--client-auth-line);
  background: var(--client-auth-surface);
  color: var(--client-auth-fg);
}

.client-login .client-login__monogram {
  display: grid;
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  place-items: center;
  border-radius: 50%;
  background: var(--client-auth-accent);
  color: var(--client-auth-accent-ink);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.client-login .client-login__brand-copy {
  min-width: 0;
}

.client-login .client-login__title {
  margin: 0;
  color: var(--client-auth-fg);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: clamp(18px, 5vw, 24px);
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.03em;
  word-break: break-word;
}

.client-login .client-login__phone,
.client-login .client-login__email {
  display: flex;
  width: fit-content;
  align-items: center;
  min-height: 44px;
  color: var(--client-auth-accent);
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.client-login .v-btn.primary {
  background: var(--client-auth-accent) !important;
  color: var(--client-auth-accent-ink) !important;
  border-radius: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.client-login .client-login__phone:focus-visible,
.client-login .client-login__email:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* #c62828 на белом — 5,6:1 (REQ-13); штатный error #ff5252 — 3,4:1. */
.client-login .client-login__error {
  color: #c62828;
  font-size: 0.95rem;
  margin: 0 0 16px;
}

.theme--dark .client-login .client-login__error {
  color: #ef9a9a;
}

/* REQ-13: поле в ошибке Vuetify красит подпись и рамку штатным error (#ff5252, 3,4:1) через .error--text с !important. */
.client-login .error--text {
  color: #c62828 !important;
  caret-color: #c62828 !important;
}

.theme--dark .client-login .error--text {
  color: #ef9a9a !important;
  caret-color: #ef9a9a !important;
}
</style>
